<script setup lang="ts">
import FooterBar from '@/components/footer-bar/FooterBar.vue'
import { useUserStore } from '@/stores/modules/user'
import { computed, ref } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import { socketEmitAsync } from '@/utils/web-socket'
import type { ItemType } from '@/types/item'
import { formatNumberWithCommas } from '@/utils'
import { getSkillName, skillCollection, getSkillNameById } from '@/core/skill'
import { getSkillLevelByExp, getSkillExpByQuality, getNextLevelExpByExp } from '@/core/skill/exp'
import type { SkillItemType } from '@/types/skill'
import SkillInfo from '@/views/item/components/SkillInfo.vue'

const userStore = useUserStore()

const allSkillItemList = computed(() => {
  return Object.entries(skillCollection).map(([id, item]) => {
    return {
      id,
      skillId: Number(id),
      quality: item.quality[item.quality.length - 1]!,
      attrs: [],
      isLock: false,
    }
  })
})

const choiceSkillId = ref<number>()
const skillInfo = ref<SkillItemType>()
const isShowSkillInfo = ref(false)
function handleChoiceUpgradeSkill(item: SkillItemType) {
  skillInfo.value = item
  if (isShowSkillInfo.value) return
  choiceSkillId.value = item.skillId
}
const userSkillItemList = computed(() => {
  if (!skillInfo.value) return []
  return userStore.skillList.filter(
    (item) =>
      item.skillId === skillInfo.value?.skillId &&
      !beUserdSkillIdList.value.includes(item.id) &&
      !item.isLock &&
      !userStore.skillEquippedList.includes(item.id),
  )
})

const beUserdSkillIdList = ref<string[]>([])
const beUserdSkillItemList = computed(() => {
  return userStore.skillList.filter((item) => {
    return beUserdSkillIdList.value.includes(item.id)
  })
})

async function handleAction(skillItem: SkillItemType, type: 'putIn' | 'takeOut') {
  if (isShowSkillInfo.value) return (skillInfo.value = skillItem)
  const { id } = skillItem
  if (!userStore.warehouseItemList) userStore.warehouseItemList = []
  if (type === 'putIn') {
    beUserdSkillIdList.value.push(id)
  } else {
    const index = beUserdSkillIdList.value.findIndex((item) => item === id)
    if (index === -1) return
    beUserdSkillIdList.value.splice(index, 1)
  }
}

const beUserdSkillExp = computed(() => {
  let exp = beUserdSkillItemList.value.reduce((total, item) => {
    const exp = getSkillExpByQuality(item.quality)
    return total + exp
  }, 0)
  exp += useUpgradeStoneCount.value * 10
  return exp
})
const curSkillExp = computed(() => {
  if (!skillInfo.value) return 0
  return userStore.skillExpList[skillInfo.value.skillId] || 0
})

const useUpgradeStoneCount = ref(0)
function handleUseUpgradeStone() {
  if (!choiceSkillId.value) return ElMessage.warning('请先选择要升级的技能')
  const skillUpgradeStone = userStore.consumables.skillUpgradeStone
  if (skillUpgradeStone <= 0) return ElMessage.warning('您未拥有技能升级石')
  ElMessageBox.prompt(`请输入您要使用的技能升级石数量（当前拥有：🟩 ${formatNumberWithCommas(skillUpgradeStone)}）`, '使用技能升级石', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    inputType: 'number',
    confirmButtonClass: 'is-plain',
    inputValue: useUpgradeStoneCount.value ? String(useUpgradeStoneCount.value) : String(skillUpgradeStone),
    beforeClose(action, instance, done) {
      if (action !== 'confirm') return done()
      const count = Number(instance.inputValue)
      if (isNaN(count) || count < 0) return ElMessage.warning('请输入正确的数量')
      if (count > userStore.consumables.skillUpgradeStone) return ElMessage.warning('您的技能升级石数量不足')
      useUpgradeStoneCount.value = count
      done()
    },
  })
}

function handleClear() {
  useUpgradeStoneCount.value = 0
  beUserdSkillIdList.value = []
}
function handleCancel() {
  skillInfo.value = undefined
  choiceSkillId.value = undefined
  handleClear()
}
function handleUpgrade() {
  if (!skillInfo.value) return
  const skillId = skillInfo.value.skillId
  const price = beUserdSkillExp.value * 10000
  if (userStore.gold < price) return ElMessage.warning('您的金币不足')
  const hasSkillBookInBackpack = userStore.skillList.some((item) => item.skillId === skillId && !beUserdSkillIdList.value.includes(item.id))
  if (!hasSkillBookInBackpack) return ElMessage.warning('请至少保留一本技能书')
  ElMessageBox.confirm(
    `<p class="affix">您确定要升级【<span class="info">${getSkillNameById(
      skillId,
      true,
    )}</span>】吗？（该操作将导致材料台中的技能书<span class="shrink">全部销毁</span>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      await socketEmitAsync<ItemType>({
        type: 'upgradeSkill',
        data: { skillId, skillItemIdList: beUserdSkillIdList.value, useUpgradeStoneCount: useUpgradeStoneCount.value },
      })
      userStore.gold -= price
      userStore.consumables.skillUpgradeStone -= useUpgradeStoneCount.value
      const newExp = curSkillExp.value + beUserdSkillExp.value
      userStore.skillExpList[skillId] = newExp
      for (const id of beUserdSkillIdList.value) {
        const index = userStore.skillList.findIndex((item) => item.id === id)
        if (index !== -1) {
          userStore.skillList.splice(index, 1)
        }
      }
      handleCancel()
      ElMessage.success('升级成功')
    })
    .catch(() => {})
}
</script>

<template>
  <div class="disassemble">
    <p class="header">
      <el-popover placement="bottom" width="100%" trigger="click">
        <template #reference>
          <el-icon size="18"><QuestionFilled /></el-icon>
        </template>
        <div class="affix">
          <h4>升级说明</h4>
          <p>1. 选取您希望升级的技能，并放入您背包中的技能书作为材料，可进行技能升级。</p>
          <p>2. 技能书给予的经验由其<span class="info">品质</span>决定。</p>
          <p>3. 可使用<span class="info">技能升级石</span>进行升级，每一块增加 10经验。</p>
          <p>4. 升级时，每 <span class="grow">1</span>经验需花费<span class="gold">10,000</span>金币。</p>
          <p>5. 随着技能等级提升，所需的经验值将越来越多，技能最高 <span class="grow">50</span>级。</p>
        </div>
      </el-popover>
      <span>
        <span style="margin-right: 5px">💰 {{ formatNumberWithCommas(userStore.gold) }}</span>
        <span>🟩 {{ formatNumberWithCommas(userStore.consumables.skillUpgradeStone) }}</span>
      </span>
    </p>
    <div class="main">
      <div class="item-list">
        <div class="border-wrap">
          <h4>{{ choiceSkillId ? '选择升级材料' : '升级列表' }}</h4>
          <div style="display: flex; align-items: center; padding: 0 5px">
            <span style="font-size: 12px">查看技能信息：</span>
            <el-switch v-model="isShowSkillInfo" size="small" />
          </div>
          <div class="item-btn-wrap btn-wrap" v-show="!choiceSkillId">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of allSkillItemList" :key="item.id">
                <CommonBtn
                  size="small"
                  @done="handleChoiceUpgradeSkill(item)"
                  :style="{ position: 'relative', borderColor: skillInfo?.id === item.id ? 'red' : '' }"
                >
                  <span :class="item.quality" v-html="getSkillName(item, false, 2, false)"> </span>
                </CommonBtn>
              </el-col>
            </el-row>
          </div>
          <div class="item-btn-wrap btn-wrap" v-show="choiceSkillId">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of userSkillItemList" :key="item.id">
                <CommonBtn
                  size="small"
                  @done="handleAction(item, 'putIn')"
                  :style="{ position: 'relative', borderColor: skillInfo?.id === item.id ? 'red' : '' }"
                >
                  <span :class="item.quality" v-html="getSkillName(item)"></span>
                </CommonBtn>
              </el-col>
            </el-row>
          </div>
        </div>
        <div class="border-wrap" style="padding: 10px; min-height: 50%; margin-top: 5px" v-show="isShowSkillInfo">
          <h4>技能信息</h4>
          <template v-if="skillInfo">
            <SkillInfo :skill-info="skillInfo" :show-action="false" :show-lock="false" :show-level-type="!choiceSkillId ? 2 : 1" />
          </template>
        </div>
      </div>
      <div class="item-list">
        <div class="action border-wrap">
          <h4>操作</h4>
          <template v-if="choiceSkillId && skillInfo">
            <el-popover placement="bottom" width="300px" trigger="click">
              <template #reference>
                <CommonBtn size="small" style="width: 100%; margin-bottom: 10px">
                  <span>
                    {{ getSkillNameById(choiceSkillId) }}
                  </span>
                </CommonBtn>
              </template>
              <SkillInfo :skill-info="skillInfo" :show-action="false" />
            </el-popover>
          </template>
          <template v-if="skillInfo">
            <div class="upgrade-tip">
              <p>获取经验：{{ formatNumberWithCommas(beUserdSkillExp) }}</p>
              <p>等级提升：Lv.{{ getSkillLevelByExp(curSkillExp) }} → Lv.{{ getSkillLevelByExp(curSkillExp + beUserdSkillExp) }}</p>
              <p>升级所需经验：{{ formatNumberWithCommas(getNextLevelExpByExp(curSkillExp)) }}</p>
              <p class="affix">
                当前加成：<span class="grow">{{ (userStore.getSkillLevelAddtion(skillInfo.skillId) - 1).toFixed(2) }}%</span>
              </p>
              <p>消耗技能石：{{ formatNumberWithCommas(useUpgradeStoneCount) }}</p>
              <p>消耗金币：{{ formatNumberWithCommas(beUserdSkillExp * 10000) }}</p>
            </div>
          </template>
          <el-button type="success" :disabled="!beUserdSkillIdList.length && !useUpgradeStoneCount" plain size="small" @click="handleUpgrade"
            >升级</el-button
          >
          <el-button type="primary" :disabled="!Boolean(choiceSkillId)" plain size="small" @click="handleUseUpgradeStone">使用技能石</el-button>
          <el-button type="warning" plain size="small" @click="handleClear">清空材料台</el-button>
          <el-button type="danger" plain size="small" @click="handleCancel" v-show="skillInfo">取消</el-button>
        </div>
        <div class="border-wrap">
          <h4>材料台</h4>
          <div class="item-btn-wrap btn-wrap">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of beUserdSkillItemList" :key="item.id">
                <CommonBtn
                  size="small"
                  @done="handleAction(item, 'takeOut')"
                  :style="{ borderColor: item?.id && skillInfo?.id === item.id ? 'red' : '' }"
                >
                  <span :class="item?.quality" v-html="getSkillName(item, false, 0, false)"> </span>
                </CommonBtn>
              </el-col>
            </el-row>
          </div>
        </div>
      </div>
    </div>
    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.disassemble {
  padding-top: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .header {
    display: flex;
    justify-content: space-between;
    margin: 0 0 5px 0;
    padding: 0 15px;
    font-size: 14px;
  }
  .item-filter {
    margin-bottom: 10px;
    display: flex;
    .el-button {
      margin-left: 10px;
    }
  }
  .main {
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    flex: 1;
  }
  .item-list {
    width: calc(50% - 5px);
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    .item-btn-wrap {
      overflow: auto;
      :deep() {
        .el-button:focus,
        .el-button:hover {
          background-color: unset;
        }
      }
    }
    .border-wrap {
      overflow: hidden;
      display: flex;
      flex-flow: column nowrap;
      height: 100%;
      &.action {
        height: auto;
        margin-bottom: 10px;
        padding: 0 10px 10px 10px;
        flex-shrink: 0;
        .el-button {
          margin-bottom: 10px;
          margin-left: 0;
        }
      }
    }
  }
  .upgrade-tip {
    font-size: 13px;
    p {
      margin: 4px 0;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
}
</style>
